import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { isWhiteLabel } from "../services/agencyService";
import onboardingService, {
  LaunchChecklistData,
  LaunchChecklistItem,
  BrandKitFile,
} from "../services/onboardingService";
import SubscriberImportModal from "./SubscribersImportModal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Calendar, FileText, ExternalLink, Pencil, Check, X } from "lucide-react";
import NewsletterStatusPill from "./NewsletterStatusPill";
import { NewsletterEditionStatus } from "../types/newsletterEdition";

type ChecklistStatus = "complete" | "in_progress" | "action_needed" | "preparing";

const getChecklistStatus = (item: LaunchChecklistItem): ChecklistStatus => {
  if (!item.edition) return "preparing";

  const editionStatus = item.edition.status;

  if (editionStatus === "Approved" || editionStatus === "Sending" || editionStatus === "Published") {
    return "complete";
  }

  if (editionStatus === "Awaiting Approval") {
    return "action_needed";
  }

  if (editionStatus === "Preparing" || editionStatus === "Finalizing") {
    return "in_progress";
  }

  return "preparing";
};

const getEmailListStatus = (complete: boolean): ChecklistStatus => {
  return complete ? "complete" : "action_needed";
};

const statusConfig = {
  complete: {
    label: "Complete",
    bgColor: "#dcfce7",
    textColor: "#166534",
  },
  in_progress: {
    label: "In Progress",
    bgColor: "#fef9c3",
    textColor: "#854d0e",
  },
  action_needed: {
    label: "Action Needed",
    bgColor: "#fee2e2",
    textColor: "#991b1b",
  },
  preparing: {
    label: "Preparing",
    bgColor: "#f3f4f6",
    textColor: "#6b7280",
  },
};

const checklistConfig = {
  brand: { label: "Brand", emoji: "🎨" },
  template: { label: "Template", emoji: "📐" },
  first_edition: { label: "First Edition", emoji: "✍️" },
  launch_email: { label: "Launch Email", emoji: "🚀" },
  email_list: { label: "Email List", emoji: "📬" },
};

const StatusPill: React.FC<{ status: ChecklistStatus }> = ({ status }) => {
  const config = statusConfig[status];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 12px",
        borderRadius: "9999px",
        backgroundColor: config.bgColor,
        color: config.textColor,
        fontSize: "14px",
        fontWeight: 500,
      }}
    >
      {config.label}
    </span>
  );
};

const ChecklistRow: React.FC<{
  itemKey: keyof typeof checklistConfig;
  item: LaunchChecklistItem;
}> = ({ itemKey, item }) => {
  const config = checklistConfig[itemKey];
  const status = getChecklistStatus(item);

  return (
    <div style={{ padding: "16px 0" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "12px",
        }}
      >
        <span style={{ fontSize: "20px" }}>{config.emoji}</span>
        <div style={{ fontWeight: 500 }}>{config.label}</div>
        <StatusPill status={status} />
      </div>

      {item.edition && (
        <li className="newsletter-item" style={{ position: 'relative' }}>
          <Link
            to={`/newsletterEdition/${item.edition.id}`}
            style={{
              textDecoration: 'none',
              color: 'inherit',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              padding: 'inherit'
            }}>
            <div className="newsletter-item-content-left">
              <span className="newsletter-title">
                {item.edition.title}
              </span>
            </div>
          </Link>

          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            position: 'absolute',
            right: '0',
            top: '50%',
            transform: 'translateY(-50%)',
            paddingRight: '16px'
          }}>
            {item.edition.scheduled_send_start_date && (
              <span
                className="newsletter-status"
                style={{backgroundColor: "#dbeafe", color: "#1d4ed8"}}>
                <span className="newsletter-status-icon">
                  <Calendar size={16} />
                </span>
                <span>{new Date(item.edition.scheduled_send_start_date).toLocaleDateString('en-US', {
                  month: '2-digit',
                  day: '2-digit',
                  year: 'numeric'
                })}</span>
              </span>
            )}

            <NewsletterStatusPill
              status={item.edition.status as NewsletterEditionStatus}
              handleUpdate={async () => {}}
            />
          </div>
        </li>
      )}

      {!item.edition && (
        <div
          style={{
            padding: "24px",
            border: "2px dashed #d1d5db",
            borderRadius: "6px",
            color: "#9ca3af",
            textAlign: "center",
          }}
        >
          Coming soon...
        </div>
      )}

      {item.notes && status !== "complete" && (
        <div
          style={{
            marginTop: "12px",
            padding: "16px 20px",
            backgroundColor: "#eff6ff",
            border: "1px solid #93c5fd",
            borderRadius: "12px",
            fontSize: "15px",
            color: "#475569",
          }}
        >
          <span style={{ fontWeight: 700 }}>Message{isWhiteLabel() ? "" : " from Potions"}:</span> {item.notes}
        </div>
      )}
    </div>
  );
};

const EmailListRow: React.FC<{ complete: boolean; onUploadClick: () => void }> = ({ complete, onUploadClick }) => {
  const config = checklistConfig.email_list;
  const status = getEmailListStatus(complete);

  return (
    <div style={{ padding: "16px 0" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "12px",
        }}
      >
        <span style={{ fontSize: "20px" }}>{config.emoji}</span>
        <div style={{ fontWeight: 500 }}>{config.label}</div>
        <StatusPill status={status} />
      </div>

      <div style={{ padding: "12px", backgroundColor: "#f9fafb", borderRadius: "6px", textAlign: "center" }}>
        <button
          onClick={onUploadClick}
          className="tertiary-info-button"
          style={{ cursor: "pointer" }}
        >
          {complete ? "📤 Upload more subscribers" : "📤 Upload your email list"}
        </button>
      </div>
    </div>
  );
};

const BrandRow: React.FC<{
  brandWebsiteUrl: string | null;
  brandKitFiles: BrandKitFile[];
  onEditClick: () => void;
}> = ({ brandWebsiteUrl, brandKitFiles, onEditClick }) => {
  const config = checklistConfig.brand;
  const isComplete = !!(brandWebsiteUrl || brandKitFiles.length > 0);
  const status: ChecklistStatus = isComplete ? "complete" : "action_needed";

  return (
    <div style={{ padding: "16px 0" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "12px",
        }}
      >
        <span style={{ fontSize: "20px" }}>{config.emoji}</span>
        <div style={{ fontWeight: 500 }}>{config.label}</div>
        <StatusPill status={status} />
        {isComplete && (
          <button
            onClick={onEditClick}
            style={{
              padding: "4px",
              borderRadius: "4px",
              border: "none",
              backgroundColor: "transparent",
              cursor: "pointer",
              color: "#9CA3AF",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Pencil size={14} />
          </button>
        )}
      </div>

      {isComplete ? (
        <div
          style={{
            padding: "16px 20px",
            backgroundColor: "#f0fdf4",
            border: "1px solid #86efac",
            borderRadius: "10px",
            fontSize: "14px",
            color: "#166534",
          }}
        >
          {brandWebsiteUrl && (
            <div style={{ marginBottom: brandKitFiles.length > 0 ? "8px" : 0 }}>
              🌐 <strong>Website:</strong> {brandWebsiteUrl}
            </div>
          )}
          {brandKitFiles.length > 0 && (
            <div>
              📁 <strong>{brandKitFiles.length} file{brandKitFiles.length !== 1 ? "s" : ""} uploaded</strong>
            </div>
          )}
        </div>
      ) : (
        <div style={{ padding: "12px", backgroundColor: "#f9fafb", borderRadius: "6px", textAlign: "center" }}>
          <button
            onClick={onEditClick}
            className="tertiary-info-button"
            style={{ cursor: "pointer" }}
          >
            🖌️ Add your brand
          </button>
        </div>
      )}
    </div>
  );
};

const Launch: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isOnboarding = location.pathname.includes('onboarding');
  const [data, setData] = useState<LaunchChecklistData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [isUpdatingDate, setIsUpdatingDate] = useState(false);
  const [editingStrategyUrl, setEditingStrategyUrl] = useState(false);
  const [strategyUrlInput, setStrategyUrlInput] = useState("");

  const fetchData = async () => {
    try {
      const data = await onboardingService.getLaunchChecklist();
      setData(data);
    } catch (err) {
      setError("Failed to load launch checklist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleTargetDateChange = async (date: Date | null): Promise<void> => {
    if (!date) return;

    const currentDate = data?.target_launch_date
      ? new Date(data.target_launch_date).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric"
      })
      : "not set";

    const newDate = date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });

    const confirmed = window.confirm(
      `Are you sure you want to change the target launch date from ${currentDate} to ${newDate}?`
    );

    if (!confirmed) return;

    const isoDate = date.toISOString();
    setIsUpdatingDate(true);

    try {
      await onboardingService.updateLaunchChecklist(isoDate);
      await fetchData();
    } catch (error) {
      console.error("Error updating target launch date:", error);
      alert("Failed to update target launch date");
    } finally {
      setIsUpdatingDate(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}>
        Loading launch checklist...
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h1>Launch</h1>
        <div
          style={{
            marginTop: "24px",
            padding: "20px",
            backgroundColor: "#fef2f2",
            borderRadius: "8px",
            color: "#991b1b",
          }}
        >
          Failed to load launch checklist. Please refresh the page or contact us.
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <h1>Launch</h1>
        <div style={{ marginTop: "24px", color: "#6b7280" }}>
          No checklist data available.
        </div>
      </div>
    );
  }

  const { checklist } = data;
  const brandComplete = !!(data.brand_website_url || data.brand_kit_files?.length > 0);
  const completedCount = [
    brandComplete,
    checklist.template.complete,
    checklist.first_edition.complete,
    checklist.launch_email.complete,
    checklist.email_list.complete,
  ].filter(Boolean).length;

  const progressPercent = (completedCount / 5) * 100;

  const welcomeMessage = completedCount === 5
    ? (data.first_name ? `Congrats, ${data.first_name} 🎉` : "Congrats 🎉")
    : (data.first_name ? `Welcome, ${data.first_name} 👋` : "Welcome 👋");

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {!isOnboarding && <h1 style={{ display: "inline-block" }}>{welcomeMessage}</h1>}
        {!isOnboarding && (
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginLeft: "auto" }}>
            <DatePicker
              selected={data.target_launch_date ? new Date(data.target_launch_date) : null}
              onChange={handleTargetDateChange}
              dateFormat="M/d/yyyy"
              popperPlacement="bottom"
              minDate={new Date()}
              customInput={
                <button className="tertiary-info-button" style={{ cursor: "pointer", width: "220px", textAlign: "center" }}>
                  {isUpdatingDate
                    ? "Target Launch: Updating..."
                    : data.target_launch_date
                      ? `Target Launch: ${new Date(data.target_launch_date).toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" })}`
                      : "Target Launch: Select date"
                  }
                </button>
              }
            />
            <button className="tertiary-info-button" style={{ cursor: "pointer" }} onClick={() => navigate("/onboarding")}>
              Onboarding
            </button>
            <button className="tertiary-info-button" style={{ cursor: "pointer" }} onClick={() => navigate("/templates")}>
              View Templates
            </button>
            <button
              className="primary-button"
              onClick={() => window.open("mailto:team@withpotions.com", "_blank")}
            >
              Contact Us
            </button>
          </div>
        )}
      </div>
      {!isOnboarding && (
        <>
          <p style={{ marginTop: "0", marginBottom: "24px" }}>
            {completedCount === 5
              ? "You're all set! Time to launch your newsletter."
              : "You're almost ready to go — just a few more steps to finalize your launch!"}
          </p>

          {/* Progress Bar */}
          <div style={{ marginBottom: "28px" }}>
            <div
              style={{
                width: "100%",
                height: "8px",
                backgroundColor: "#e5e7eb",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${progressPercent}%`,
                  height: "100%",
                  backgroundColor: "#22c55e",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
            <p style={{ marginTop: "8px", color: "#6b7280", fontSize: "14px" }}>
              {completedCount} of 5 steps complete ({Math.round(progressPercent)}%)
            </p>
          </div>
        </>
      )}

      {/* Strategy Document */}
      {!isOnboarding && (
        <div style={{
          padding: "16px 20px", marginBottom: "24px", borderRadius: "10px",
          border: "1px solid #E5E7EB", backgroundColor: "#F9FAFB",
          display: "flex", alignItems: "center", gap: "12px",
        }}>
          <FileText size={20} color="#6366F1" style={{ flexShrink: 0 }} />
          {editingStrategyUrl ? (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
              <input
                type="url"
                value={strategyUrlInput}
                onChange={(e) => setStrategyUrlInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    onboardingService.saveStrategyDocumentUrl(strategyUrlInput).then(() => {
                      fetchData();
                      setEditingStrategyUrl(false);
                    });
                  }
                }}
                placeholder="Paste Google Doc link..."
                autoFocus
                style={{
                  flex: 1, padding: "6px 10px", borderRadius: "6px",
                  border: "1px solid #D1D5DB", fontSize: "14px", outline: "none",
                }}
              />
              <button
                onClick={() => {
                  onboardingService.saveStrategyDocumentUrl(strategyUrlInput).then(() => {
                    fetchData();
                    setEditingStrategyUrl(false);
                  });
                }}
                style={{
                  padding: "6px 8px", borderRadius: "6px", border: "none",
                  backgroundColor: "#6366F1", color: "#fff", cursor: "pointer",
                  display: "flex", alignItems: "center",
                }}
              >
                <Check size={16} />
              </button>
              <button
                onClick={() => setEditingStrategyUrl(false)}
                style={{
                  padding: "6px 8px", borderRadius: "6px", border: "1px solid #D1D5DB",
                  backgroundColor: "#fff", cursor: "pointer",
                  display: "flex", alignItems: "center",
                }}
              >
                <X size={16} />
              </button>
            </div>
          ) : data.strategy_document_url ? (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
              <a
                href={data.strategy_document_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: "14px", fontWeight: 500, color: "#6366F1",
                  textDecoration: "none", display: "flex", alignItems: "center", gap: "4px",
                }}
              >
                Strategy Document <ExternalLink size={14} />
              </a>
              <button
                onClick={() => {
                  setStrategyUrlInput(data.strategy_document_url || "");
                  setEditingStrategyUrl(true);
                }}
                style={{
                  padding: "4px", borderRadius: "4px", border: "none",
                  backgroundColor: "transparent", cursor: "pointer", color: "#9CA3AF",
                  display: "flex", alignItems: "center",
                }}
              >
                <Pencil size={14} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                setStrategyUrlInput("");
                setEditingStrategyUrl(true);
              }}
              style={{
                fontSize: "14px", color: "#6366F1", fontWeight: 500,
                background: "none", border: "none", cursor: "pointer", padding: 0,
              }}
            >
              Add Strategy Document link
            </button>
          )}
        </div>
      )}

      {/* Checklist */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {!isOnboarding && (
          <BrandRow
            brandWebsiteUrl={data.brand_website_url}
            brandKitFiles={data.brand_kit_files || []}
            onEditClick={() => navigate("/brand")}
          />
        )}
        <ChecklistRow itemKey="template" item={checklist.template} />
        <ChecklistRow itemKey="first_edition" item={checklist.first_edition} />
        <ChecklistRow itemKey="launch_email" item={checklist.launch_email} />
        {!isOnboarding && (
          <EmailListRow
            complete={checklist.email_list.complete}
            onUploadClick={() => setShowImportModal(true)}
          />
        )}
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1000,
            }}
            onClick={() => setShowImportModal(false)}
          />
          <SubscriberImportModal
            onClose={() => setShowImportModal(false)}
            onSuccess={() => {
              setShowImportModal(false);
              // Refresh the data
              onboardingService.getLaunchChecklist().then(setData);
            }}
          />
        </>
      )}

    </div>
  );
};

export default Launch;
